import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fetchTokenData, fetchOHLCV } from '../utils/tokenApi';
import { calculateRealTA, formatChartData } from '../utils/technicalAnalysis';
import { cn } from '../utils/cn';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine, ComposedChart, Bar, Cell } from 'recharts';

const { FiSearch, FiActivity, FiTrendingUp, FiTrendingDown, FiBarChart2, FiCpu, FiTarget, FiCrosshair, FiLoader, FiAlertCircle, FiShield, FiArrowDownRight, FiLayers, FiAlignRight } = FiIcons;

const formatTokenPrice = (price) => {
  if (!price) return '0.00';
  if (price < 0.00001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const PriceTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0B0E14] border border-slate-800 p-2 rounded-lg shadow-xl">
        <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">{payload[0].payload.time}</p>
        <p className="text-white font-mono text-sm">${payload[0].value.toFixed(6)}</p>
      </div>
    );
  }
  return null;
};

const RSITooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0B0E14] border border-slate-800 p-2 rounded-lg shadow-xl">
        <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">RSI (14)</p>
        <p className="text-purple-400 font-mono text-sm">{payload[0].value.toFixed(2)}</p>
      </div>
    );
  }
  return null;
};

export default function Analytics({ defaultTimeframe, strategy = 'confluence', signalQuality = true }) {
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [timeframe, setTimeframe] = useState(defaultTimeframe || '15m'); // Defaults to the configured bot timeframe
  const [chartData, setChartData] = useState([]);
  const [ta, setTa] = useState(null);

  const handleAnalyze = async (e) => {
    e?.preventDefault();
    if (!address) return;

    setIsLoading(true);
    setError(null);

    try {
      const realToken = await fetchTokenData(address);
      setTokenData(realToken);
    } catch (err) {
      setError(err.message || 'Failed to analyze token');
      setTokenData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadRealData = async () => {
      if (!tokenData || !tokenData.pairAddress) return;
      
      setIsDataLoading(true);
      try {
        const ohlcv = await fetchOHLCV(tokenData.pairAddress, timeframe);
        const formattedChart = formatChartData(ohlcv);
        const newTa = calculateRealTA(ohlcv, tokenData.price, null, strategy, signalQuality);
        
        setChartData(formattedChart);
        setTa(newTa);
      } catch (err) {
        console.error('Failed to load OHLCV', err);
      } finally {
        setIsDataLoading(false);
      }
    };

    loadRealData();
  }, [tokenData, timeframe, strategy, signalQuality]);

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col space-y-6 pb-6 overflow-hidden">
      
      <div className="glass-panel p-4 rounded-2xl flex-shrink-0">
        <form onSubmit={handleAnalyze} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input 
              type="text" 
              value={address} 
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Paste Token Contract Address to Analyze..." 
              className="input-field pl-12"
              disabled={isLoading}
            />
          </div>
          <button 
            type="submit" 
            disabled={isLoading || !address}
            className="btn-primary sm:w-48 flex items-center justify-center gap-2"
          >
            {isLoading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiActivity} /> Analyze Token</>}
          </button>
        </form>
        {error && (
          <p className="text-red-400 text-xs flex items-center gap-1 mt-3 font-medium px-2">
            <SafeIcon icon={FiAlertCircle} /> {error}
          </p>
        )}
      </div>

      {!tokenData ? (
        <div className="flex-1 glass-panel rounded-2xl flex flex-col items-center justify-center text-slate-600">
          <SafeIcon icon={FiBarChart2} className="text-6xl opacity-10 mb-4" />
          <p className="text-lg font-medium text-slate-400">Advanced Analytics Engine</p>
          <p className="text-sm mt-2 max-w-md text-center">Paste a token address above to generate institutional-grade technical indicators based on real OHLCV data.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0 overflow-y-auto custom-scrollbar pr-2">
          
          <div className="flex-1 flex flex-col gap-4 min-h-[800px] lg:min-h-0 relative">
            {isDataLoading && (
              <div className="absolute inset-0 z-10 bg-[#0B0E14]/80 backdrop-blur-sm flex items-center justify-center rounded-xl">
                <SafeIcon icon={FiLoader} className="animate-spin text-purple-500 text-4xl" />
              </div>
            )}

            <div className="glass-panel p-4 rounded-xl flex justify-between items-center bg-gradient-to-r from-[#151924] to-[#0B0E14]">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-black text-white">{tokenData.name}</h2>
                  <span className="px-2 py-0.5 bg-slate-800 text-slate-300 rounded text-xs font-bold">{tokenData.symbol}</span>
                </div>
                <div className="text-sm text-slate-500 font-mono mt-1">{tokenData.pairAddress}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Live Price</div>
                <div className="text-2xl font-mono font-black text-green-400">${tokenData.price.toFixed(6)}</div>
              </div>
            </div>

            <div className="glass-panel p-4 rounded-xl flex-1 flex flex-col min-h-[300px]">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} /> Price Action & Volume
              </h3>
              <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="time" hide />
                    <YAxis domain={['auto', 'auto']} orientation="right" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => val.toFixed(4)} />
                    <Tooltip content={<PriceTooltip />} cursor={{ stroke: '#334155', strokeWidth: 1, strokeDasharray: '4 4' }} />
                    <Area type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPrice)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass-panel p-4 rounded-xl h-48 flex flex-col">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                <SafeIcon icon={FiActivity} /> RSI (14)
              </h3>
              <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="time" hide />
                    <YAxis domain={[0, 100]} orientation="right" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} ticks={[30, 50, 70]} />
                    <Tooltip content={<RSITooltip />} cursor={{ stroke: '#334155', strokeWidth: 1 }} />
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.5} />
                    <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.5} />
                    <Line type="monotone" dataKey="rsi" stroke="#a855f7" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass-panel p-4 rounded-xl h-48 flex flex-col">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                <SafeIcon icon={FiBarChart2} /> MACD (12, 26, 9)
              </h3>
              <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="time" hide />
                    <YAxis domain={['auto', 'auto']} orientation="right" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => val.toFixed(4)} />
                    <Tooltip cursor={{ stroke: '#334155', strokeWidth: 1 }} contentStyle={{ backgroundColor: '#0B0E14', borderColor: '#1e293b' }} />
                    <Bar dataKey="hist" opacity={0.5}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fill={entry.hist >= 0 ? '#22c55e' : '#ef4444'} />
                      ))}
                    </Bar>
                    <Line type="monotone" dataKey="macd" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="signal" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-96 flex-shrink-0 flex flex-col gap-4">
            {ta && (
              <div className="glass-panel p-5 rounded-xl border border-slate-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiCpu} className="text-purple-400 text-lg" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">AI Synthesis</h3>
                  </div>
                  <div className="flex bg-[#0B0E14] rounded-lg p-1 border border-slate-800">
                    {['1m', '5m', '15m', '1h', '4h'].map(tf => (
                      <button 
                        key={tf}
                        onClick={() => setTimeframe(tf)}
                        className={cn(
                          "px-2 py-1 text-[10px] font-bold rounded-md transition-colors",
                          timeframe === tf 
                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/20" 
                            : "text-slate-500 hover:text-slate-300"
                        )}
                      >
                        {tf}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cn("p-4 rounded-xl border flex flex-col items-center justify-center text-center gap-2 mb-6", ta.bgColor)}>
                  <SafeIcon icon={ta.signal.includes('BUY') ? FiTrendingUp : (ta.signal.includes('SELL') ? FiTrendingDown : FiActivity)} className={cn("text-4xl", ta.signalColor)} />
                  <div className={cn("text-2xl font-black tracking-widest", ta.signalColor)}>
                    {ta.signal}
                  </div>
                </div>

                <p className="text-sm text-slate-300 leading-relaxed mb-6 bg-slate-900/50 p-4 rounded-lg border border-slate-800">
                  {ta.reasoning}
                </p>

                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <SafeIcon icon={FiShield} className="text-blue-400" /> Suggested Risk Settings (ATR-Based)
                </h4>
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <div className="border border-slate-800/80 rounded bg-[#0B0E14] p-2 text-center">
                    <div className="text-[9px] text-slate-500 uppercase font-bold mb-1">Hard Stop</div>
                    <div className="text-red-400 font-mono text-sm font-bold">
                      {ta.suggestions.hardStop.pct.toFixed(2)}%
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono mt-0.5">
                      ${formatTokenPrice(ta.suggestions.hardStop.price)}
                    </div>
                  </div>
                  <div className="border border-slate-800/80 rounded bg-[#0B0E14] p-2 text-center">
                    <div className="text-[9px] text-slate-500 uppercase font-bold mb-1">Take Profit</div>
                    <div className="text-green-400 font-mono text-sm font-bold">
                      {ta.suggestions.takeProfit.pct.toFixed(2)}%
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono mt-0.5">
                      ${formatTokenPrice(ta.suggestions.takeProfit.price)}
                    </div>
                  </div>
                  <div className="border border-slate-800/80 rounded bg-[#0B0E14] p-2 text-center">
                    <div className="text-[9px] text-slate-500 uppercase font-bold mb-1">Trail Stop</div>
                    <div className="text-orange-400 font-mono text-sm font-bold">
                      {ta.suggestions.trailingStop.pct.toFixed(2)}%
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono mt-0.5">
                      Dynamic
                    </div>
                  </div>
                </div>

                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Technical Readings</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  <IndicatorTile icon={FiActivity} title="RSI (14)" value={ta.indicators.rsi.value} status={ta.indicators.rsi.status} />
                  <IndicatorTile icon={FiTrendingUp} title="MACD" value={ta.indicators.macd.status} status="Trend" />
                  <IndicatorTile icon={FiTarget} title="EMA 20" value={ta.indicators.ema.status.split(' ')[0]} status="Trend" />
                  <IndicatorTile icon={FiTarget} title="SMA 20" value={ta.indicators.sma.status.split(' ')[0]} status="Trend" />
                  <IndicatorTile icon={FiCrosshair} title="Boll Bands" value={ta.indicators.bb.status} status="Position" />
                  <IndicatorTile icon={FiActivity} title="ATR (14)" value={ta.indicators.atr.value} status="Volatility" />
                  <IndicatorTile icon={FiActivity} title="Stochastic" value={ta.indicators.stoch.status} status="Momentum" />
                  <IndicatorTile icon={FiTrendingUp} title="ADX / DI" value={ta.indicators.adx.status} status="Trend" />
                  <IndicatorTile icon={FiBarChart2} title="OBV" value={ta.indicators.obv.status} status="Volume" />
                  <IndicatorTile icon={FiBarChart2} title="Rel Vol" value={ta.indicators.relVol.value} status={ta.indicators.relVol.status} />
                  <IndicatorTile icon={FiTarget} title="VWAP" value={ta.indicators.vwap.status.split(' ')[0]} status="Position" />
                </div>

                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 mt-6 flex items-center gap-1.5">
                  <SafeIcon icon={FiLayers} className="text-blue-400" /> Key Price Levels
                </h4>
                <div className="grid grid-cols-1 gap-4 mb-6">
                  <SupportResistancePanel sr={ta.supportResistance} formatPrice={formatTokenPrice} />
                  <FibonacciPanel fib={ta.fibonacci} formatPrice={formatTokenPrice} />
                </div>

              </div>
            )}

            <div className="glass-panel p-5 rounded-xl border border-slate-700/50">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <SafeIcon icon={FiBarChart2} className="text-blue-400" /> Market Data
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800/50 pb-3">
                  <span className="text-xs text-slate-400 uppercase font-bold">24h Volume</span>
                  <span className="text-sm text-white font-mono font-bold">${tokenData.volume24h.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pb-1">
                  <span className="text-xs text-slate-400 uppercase font-bold">Liquidity (USD)</span>
                  <span className="text-sm text-white font-mono font-bold">${tokenData.liquidity.toLocaleString()}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

function IndicatorTile({ icon, title, value, status }) {
  let color = "text-slate-300";
  if (value.includes('Overbought') || value.includes('Bearish') || value.includes('Upper') || value.includes('Below') || value.includes('Distribution') || value.includes('Declining')) color = "text-red-400";
  if (value.includes('Oversold') || value.includes('Bullish') || value.includes('Lower') || value.includes('Above') || value.includes('Accumulation') || value.includes('Surging')) color = "text-green-400";

  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <SafeIcon icon={icon} className="text-[10px] text-slate-500" />
        <span className="text-[10px] font-bold text-slate-500 uppercase">{title}</span>
      </div>
      <div className={cn("text-xs font-bold truncate", color)} title={value}>{value}</div>
      <div className="text-[9px] text-slate-600 uppercase mt-1">{status}</div>
    </div>
  );
}

function SupportResistancePanel({ sr, formatPrice }) {
  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3">
      <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <SafeIcon icon={FiLayers} className="text-blue-400" /> Support & Resistance
      </h5>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[9px] text-red-400 font-bold uppercase mb-1">Resistance</div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">R3</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.r3)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">R2</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.r2)}</span>
          </div>
          <div className="flex justify-between items-center bg-red-500/10 border border-red-500/20 px-2 py-1.5 rounded">
            <span className="text-[10px] text-red-400 font-bold">R1</span>
            <span className="text-xs font-mono text-red-400">${formatPrice(sr.r1)}</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="text-[9px] text-green-400 font-bold uppercase mb-1">Support</div>
          <div className="flex justify-between items-center bg-green-500/10 border border-green-500/20 px-2 py-1.5 rounded">
            <span className="text-[10px] text-green-400 font-bold">S1</span>
            <span className="text-xs font-mono text-green-400">${formatPrice(sr.s1)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">S2</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.s2)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">S3</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.s3)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-between items-center bg-blue-500/10 border border-blue-500/20 px-3 py-2 rounded-lg">
        <span className="text-[10px] text-blue-400 font-bold uppercase">Pivot Point</span>
        <span className="text-sm font-mono text-blue-400 font-bold">${formatPrice(sr.pivot)}</span>
      </div>
    </div>
  );
}

function FibonacciPanel({ fib, formatPrice }) {
  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3">
      <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <SafeIcon icon={FiAlignRight} className="text-purple-400" /> Fibonacci Retracement
      </h5>
      <div className="space-y-1.5 mt-2">
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">23.6%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl236)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">38.2%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl382)}</span>
        </div>
        <div className="flex justify-between items-center bg-purple-500/10 border border-purple-500/20 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-purple-400">50.0%</span>
          <span className="text-sm font-mono text-purple-400 font-bold">${formatPrice(fib.lvl500)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">61.8%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl618)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">78.6%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl786)}</span>
        </div>
      </div>
    </div>
  );
}